import type { Observer } from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import type AbstractSubject from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import type SearchUrlUpdater from '../../../common/search/SearchUrlUpdater';
import type DestinationSearch from '../../../ui/web-components/searchBar/DestinationSearch';
import { DestinationSearchEvents } from '../../../ui/web-components/searchBar/DestinationSearch';
import HouseSearchFilters from '../store/HouseSearchFilters';
import KeywordFilter from '../store/KeywordFilter';

export default class DestinationSearchConnector implements Observer {
    readonly #element: DestinationSearch;

    readonly #keywordFilter: KeywordFilter;

    readonly #urlUpdater: SearchUrlUpdater;

    readonly #defaultSearchPageUrl: string;

    constructor(
        element: DestinationSearch,
        keywordFilter: KeywordFilter,
        urlUpdater: SearchUrlUpdater
    ) {
        this.#element = element;
        this.#keywordFilter = keywordFilter;
        this.#urlUpdater = urlUpdater;

        this.#element.addEventListener(
            DestinationSearchEvents.DESTINATION_SELECTED,
            this.#updateState.bind(this)
        );
        this.#element.addEventListener(
            DestinationSearchEvents.DESTINATION_CLEARED,
            this.#handleClear.bind(this)
        );
        this.#defaultSearchPageUrl = this.#element.dataset.defaultSearchpage || '';
    }

    public static connectToElement(querySelector: string, urlUpdater: SearchUrlUpdater): void {
        const filters = HouseSearchFilters.getInstance();
        const element = document.querySelector(querySelector) as DestinationSearch | null;
        const keywordFilter = filters.get(KeywordFilter.NAME) as KeywordFilter | undefined;

        if (!element || !keywordFilter) return;

        const connector = new DestinationSearchConnector(element, keywordFilter, urlUpdater);
        keywordFilter.attach(connector);
    }

    update(subject: AbstractSubject): void {
        if (!(subject instanceof KeywordFilter)) return;

        if (subject.parameters.keyword === null) {
            this.#element.clear();
        }
    }

    #updateState(event: Event): void {
        const customEvent = event as CustomEvent;
        const detail = customEvent.detail;

        if (detail.isKeywordSearch) {
            this.#keywordFilter.keyword = detail.value;
            return;
        }

        const url = this.#urlUpdater.url;
        const newUrl = new URL(detail.value, url);
        newUrl.search = url.search;
        newUrl.searchParams.delete('skip');

        if (detail.type !== 'place') {
            newUrl.searchParams.delete('radius');
        }

        window.location.href = newUrl.href;
    }

    #handleClear(): void {
        if (this.#keywordFilter.parameters.keyword != null) {
            this.#keywordFilter.clear();
            return;
        }

        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete('skip');
        currentUrl.searchParams.delete('radius');

        window.location.href = `${this.#defaultSearchPageUrl}${currentUrl.search}`;
    }
}
